import { createAction } from '@ngrx/store';
import {
  CreateUpdateSecurityPlan,
  PlanStatus,
  SecurityPlanDetails,
  SystemSecurityScore,
  LocalLawsProtection,
  CriticalSystems,
  SecurityPlan,
  SecurityPlanRiskCoverage,
  ContractualCompliance,
  ComplianceCoverage,
  PlanStatusDropdownOption,
  DatesOverlap,
  PlanStrategyOption,
  StrategyOption,
  ControlPrioritizationOption,
} from 'projects/shared/src/lib/core/interfaces';
import { baseProps } from 'projects/shared/src/lib/core/store/actions-base';
import { LoadingSections } from './security-plan.reducer';

export const fetchSecurityPlans = createAction(
  '[@diso/security-plan] fetchSecurityPlans',
  baseProps<{}>(),
);

export const fetchSecurityPlansSuccess = createAction(
  '[@diso/security-plan] fetchSecurityPlansSuccess',
  baseProps<{ securityPlans: SecurityPlan[]; canManagePlans: boolean }>(),
);

export const fetchSecurityPlansFailure = createAction(
  '[@diso/security-plan] fetchSecurityPlansFailure',
);

export const fetchSystemSecurityScore = createAction(
  '[@diso/security-plan] fetchSystemSecurityScore',
  baseProps<{ bypassLoader: boolean }>(),
);

export const fetchSystemSecurityScoreSuccess = createAction(
  '[@diso/security-plan] fetchSystemSecurityScoreSuccess',
  baseProps<{ systemSecurityScore: SystemSecurityScore }>(),
);

export const fetchRiskCoverage = createAction(
  '[@diso/security-plan] fetchRiskCoverage',
  baseProps(),
);

export const fetchRiskCoverageSuccess = createAction(
  '[@diso/security-plan] fetchRiskCoverageSuccess',
  baseProps<{ riskCoverage: SecurityPlanRiskCoverage }>(),
);

export const fetchContractualCompliance = createAction(
  '[@diso/security-plan] fetchContractualCompliance',
  baseProps(),
);

export const fetchContractualComplianceSuccess = createAction(
  '[@diso/security-plan] fetchContractualComplianceSuccess',
  baseProps<{ contractualCompliance: ContractualCompliance }>(),
);

export const fetchComplianceCoverage = createAction(
  '[@diso/security-plan] fetchComplianceCoverage',
  baseProps(),
);

export const fetchComplianceCoverageSuccess = createAction(
  '[@diso/security-plan] fetchComplianceCoverageSuccess',
  baseProps<{ complianceCoverage: ComplianceCoverage }>(),
);

export const fetchCriticalSystems = createAction(
  '[@diso/security-plan] fetchCriticalSystems',
  baseProps(),
);

export const fetchCriticalSystemsSuccess = createAction(
  '[@diso/security-plan] fetchCriticalSystemsSuccess',
  baseProps<{ criticalSystems: CriticalSystems }>(),
);

export const fetchLocalLawsProtection = createAction(
  '[@diso/security-plan] fetchLocalLawsProtection',
  baseProps(),
);

export const fetchLocalLawsProtectionSuccess = createAction(
  '[@diso/security-plan] fetchLocalLawsProtectionSuccess',
  baseProps<{ localLawsProtection: LocalLawsProtection }>(),
);

export const changeLoadingSection = createAction(
  '[@diso/security-plan] changeLoadingSection',
  baseProps<{ section: keyof LoadingSections; loading: boolean }>(),
);

export const updatePlanStatus = createAction(
  '[@diso/security-plan] updatePlanStatus',
  baseProps<{ id: string; status: PlanStatus; plan?: SecurityPlan }>(),
);

export const updatePlanStatusSuccess = createAction(
  '[@diso/security-plan] updatePlanStatusSuccess',
  baseProps<{ id: string; status: PlanStatus }>(),
);

export const updatePlanStatusFailure = createAction(
  '[@diso/security-plan] updatePlanStatusFailure',
  baseProps<{ id: string }>(),
);

export const storeUpdatePlanStatusDetails = createAction(
  '[@diso/security-plan] storeUpdatePlanStatusDetails',
  baseProps<{ id: string; status: PlanStatus }>(),
);

export const toggleDetailsSidebar = createAction(
  '[@diso/security-plan] toggleDetailsSidebar',
  baseProps<{ show: boolean }>(),
);

export const fetchPlanDetails = createAction(
  '[@diso/security-plan] fetchPlanDetails',
  baseProps<{ id: string }>(),
);

export const fetchPlanDetailsSuccess = createAction(
  '[@diso/security-plan] fetchPlanDetailsSuccess',
  baseProps<{ securityPlanDetails: SecurityPlanDetails }>(),
);

export const fetchPlanDetailsFailure = createAction(
  '[@diso/security-plan] fetchPlanDetailsFailure',
);

export const updateCreateSecurityPlan = createAction(
  '[@diso/security-plan] updateCreateSecurityPlan',
  baseProps<{ securityPlan: CreateUpdateSecurityPlan }>(),
);

export const updateSecurityPlanSuccess = createAction(
  '[@diso/security-plan] updateSecurityPlanSuccess',
  baseProps<{ planId: string }>(),
);

export const removeSecurityPlanDetailsSidebar = createAction(
  '[@diso/security-plan] removeSecurityPlanDetailsSidebar',
);

export const toggleEditMode = createAction(
  '[@diso/security-plan] toggleEditMode',
  baseProps<{ isEditMode: boolean }>(),
);

export const highLightPlan = createAction(
  '[@diso/security-plan] highLightPlan',
  baseProps<{ planId: string }>(),
);

export const fetchDatesOverlap = createAction(
  '[@diso/security-plan] fetchDatesOverlap',
  baseProps<{ startingDate: string; planLength: number; planId?: string }>(),
);

export const fetchDatesOverlapSuccess = createAction(
  '[@diso/security-plan] fetchDatesOverlapSuccess',
  baseProps<{ datesOverlap: DatesOverlap }>(),
);

export const fetchDatesChangeRequired = createAction(
  '[@diso/security-plan] fetchDatesChangeRequired',
  baseProps<{ startingDate: string; planLength: number; planId: string }>(),
);

export const fetchDatesChangeRequiredSuccess = createAction(
  '[@diso/security-plan] fetchDatesChangeRequiredSuccess',
  baseProps<{ hasImpact: boolean; messageId: string }>(),
);

export const removeDatesChangeRequired = createAction(
  '[@diso/security-plan] removeDatesChangeRequired',
);

export const fetchPlanStatusDropdownOptions = createAction(
  '[@diso/security-plan] fetchPlanStatusDropdownOptions',
);

export const fetchPlanStatusDropdownOptionsSuccess = createAction(
  '[@diso/security-plan] fetchPlanStatusDropdownOptionsSuccess',
  baseProps<{ planStatusDropdownOptions: PlanStatusDropdownOption[] }>(),
);

export const fetchPlanStatusDropdownOptionsFailure = createAction(
  '[@diso/security-plan] fetchPlanStatusDropdownOptionsFailure',
);

export const fetchPresetBudgetTypeOptions = createAction(
  '[@diso/security-plan] fetchPresetBudgetTypeOptions',
);

export const fetchPresetBudgetTypeOptionsSuccess = createAction(
  '[@diso/security-plan] fetchPresetBudgetTypeOptionsSuccess',
  baseProps<{ presetBudgetTypeOptions: { id: string; name: string }[] }>(),
);

export const fetchPlanBudgetTypes = createAction(
  '[@diso/security-plan] fetchPlanBudgetTypes',
);

export const fetchPlanBudgetTypesSuccess = createAction(
  '[@diso/security-plan] fetchPlanBudgetTypesSuccess',
  baseProps<{ planBudgetTypes: { id: string; name: string }[] }>(),
);

export const fetchStrategyOptions = createAction(
  '[@diso/security-plan] fetchStrategyOptions',
);

export const fetchStrategyOptionsSuccess = createAction(
  '[@diso/security-plan] fetchStrategyOptionsSuccess',
  baseProps<{ strategyOptions: PlanStrategyOption<StrategyOption>[] }>(),
);

export const fetchStrategyOptionsFailure = createAction(
  '[@diso/security-plan] fetchStrategyOptionsFailure',
);

export const fetchControlPrioritizationOptions = createAction(
  '[@diso/security-plan] fetchControlPrioritizationOptions',
);

export const fetchControlPrioritizationOptionsSuccess = createAction(
  '[@diso/security-plan] fetchControlPrioritizationOptionsSuccess',
  baseProps<{
    controlPrioritizationOptions: PlanStrategyOption<ControlPrioritizationOption>[];
  }>(),
);

export const fetchControlPrioritizationOptionsFailure = createAction(
  '[@diso/security-plan] fetchControlPrioritizationOptionsFailure',
);

export const fetchPlanConfigurationParameters = createAction(
  '[@diso/security-plan] fetchPlanConfigurationParameters',
);

export const fetchPlanConfigurationParametersSuccess = createAction(
  '[@diso/security-plan] fetchPlanConfigurationParametersSuccess',
  baseProps<{
    planConfigurationParameters: PlanStrategyOption<
      ControlPrioritizationOption | StrategyOption
    >[];
  }>(),
);

export const fetchSecurityPlanNames = createAction(
  '[@diso/security-plan] fetchSecurityPlanNames',
);

export const fetchSecurityPlanNamesSuccess = createAction(
  '[@diso/security-plan] fetchSecurityPlanNamesSuccess',
  baseProps<{ securityPlanNames: { id: string; name: string }[] }>(),
);

export const fetchSecurityPlanNamesFailure = createAction(
  '[@diso/security-plan] fetchSecurityPlanNamesFailure',
);

export const changePlanStatus = createAction(
  '[@diso/security-plan] changePlanStatus',
  baseProps<{ plans: SecurityPlan[] }>(),
);
